import type { ReactNode } from 'react';
import { ErroreApi } from '../api/cliente.js';
import { t } from '../i18n/lingua.js';

/**
 * La tabella degli elenchi.
 *
 * Densità alta: righe basse, testo piccolo, zebra appena percettibile. Chi la
 * guarda ci passa la giornata e vuole vedere trenta righe insieme, non dieci
 * ariose.
 *
 * Sul telefono la stessa tabella diventa un elenco di schede — lo fa il
 * foglio di stile, non un secondo componente da tenere allineato — e per
 * riuscirci le serve una cosa sola da qui: che ogni cella si porti dietro il
 * titolo della sua colonna. Senza intestazione, «08/08/2026» da solo non
 * dice se è la data dell'ordine o quella d'arrivo.
 */

export interface Colonna<T> {
  chiave: string;
  titolo: string;
  /** Come si disegna la cella. Senza, si stampa il valore così com'è. */
  cella?: (riga: T) => ReactNode;
  ordinabile?: boolean;
  allineamento?: 'sinistra' | 'destra' | 'centro';
  /** Numeri, codici e date vanno in monospazio: si confrontano incolonnati. */
  monospazio?: boolean;
  /**
   * Larghezza massima in pixel: oltre, il testo si taglia con i puntini.
   * Serve alle colonne con dentro ragioni sociali lunghe, che altrimenti
   * spingono fuori dallo schermo le colonne che contano davvero.
   */
  larghezzaMax?: number;
  /**
   * Che fine fa questa colonna sul telefono.
   *
   * `titolo` è la riga in grande in cima alla scheda — di norma la prima
   * colonna, quella che dice di che riga si tratta. `fuori` sparisce: su uno
   * schermo da quattrocento pixel un protocollo interno ruba lo spazio al
   * cliente e allo stato.
   */
  mobile?: 'titolo' | 'fuori';
}

interface Props<T> {
  colonne: Colonna<T>[];
  righe: T[];
  chiaveRiga: (riga: T) => string | number;
  ordina?: string;
  verso?: 'asc' | 'desc';
  onOrdina?: (chiave: string) => void;
  onApri?: (riga: T) => void;
  selezione?: Set<string | number>;
  onSelezione?: (chiavi: Set<string | number>) => void;
  caricamento?: boolean;
  vuoto?: ReactNode;
  /**
   * Perché l'elenco non è arrivato. Senza, un errore del servizio sembrava un
   * elenco vuoto: «nessun risultato», e nessuno sapeva che era un errore.
   */
  errore?: unknown;
}

const CLASSE_ALLINEAMENTO = { sinistra: '', destra: 'right', centro: 'center' } as const;

/** Il valore di una cella come testo, per il suggerimento: solo se è testo. */
function testoIntero(valore: unknown): string | undefined {
  return typeof valore === 'string' || typeof valore === 'number' ? String(valore) : undefined;
}

export function Tabella<T>({
  colonne,
  righe,
  chiaveRiga,
  ordina,
  verso,
  onOrdina,
  onApri,
  selezione,
  onSelezione,
  caricamento,
  vuoto,
  errore,
}: Props<T>) {
  const conSelezione = selezione !== undefined && onSelezione !== undefined;
  const tutteSelezionate = conSelezione && righe.length > 0 && righe.every((r) => selezione.has(chiaveRiga(r)));

  const cambiaTutte = () => {
    if (!conSelezione) return;
    onSelezione(tutteSelezionate ? new Set() : new Set(righe.map(chiaveRiga)));
  };

  const cambiaUna = (chiave: string | number) => {
    if (!conSelezione) return;
    const nuova = new Set(selezione);
    if (nuova.has(chiave)) nuova.delete(chiave);
    else nuova.add(chiave);
    onSelezione(nuova);
  };

  return (
    <div className="table-scroll a-schede">
      <table className="data">
        <thead>
          <tr>
            {conSelezione && (
              <th className="col-check">
                <input
                  type="checkbox"
                  aria-label={t('Seleziona tutte le righe della pagina')}
                  checked={tutteSelezionate}
                  onChange={cambiaTutte}
                />
              </th>
            )}
            {colonne.map((colonna) => (
              <th
                key={colonna.chiave}
                className={[
                  colonna.ordinabile && onOrdina ? 'sortable' : '',
                  CLASSE_ALLINEAMENTO[colonna.allineamento ?? 'sinistra'],
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={colonna.ordinabile && onOrdina ? () => onOrdina(colonna.chiave) : undefined}
                aria-sort={
                  ordina === colonna.chiave
                    ? verso === 'desc'
                      ? 'descending'
                      : 'ascending'
                    : undefined
                }
              >
                {colonna.titolo}
                {ordina === colonna.chiave && (
                  <span className="sort">{verso === 'desc' ? '▼' : '▲'}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {caricamento && righe.length === 0 && (
            <tr>
              <td colSpan={colonne.length + (conSelezione ? 1 : 0)} className="empty">
                {t('Un momento…')}
              </td>
            </tr>
          )}

          {!caricamento && righe.length === 0 && (
            <tr>
              <td
                colSpan={colonne.length + (conSelezione ? 1 : 0)}
                className="empty"
                style={errore ? { color: 'var(--tomato)' } : undefined}
              >
                {errore
                  ? errore instanceof ErroreApi
                    ? errore.message
                    : t('L’elenco non è arrivato: riprova fra un momento.')
                  : (vuoto ?? t('Nessun risultato.'))}
              </td>
            </tr>
          )}

          {righe.map((riga) => {
            const chiave = chiaveRiga(riga);
            return (
              <tr
                key={chiave}
                aria-selected={conSelezione ? selezione.has(chiave) : undefined}
                onDoubleClick={
                  onApri
                    ? () => onApri(riga)
                    : (e) => {
                        // senza un'azione propria, il doppio clic apre quello
                        // che apre il nome: prima non succedeva niente
                        if ((e.target as HTMLElement).closest('a, button, input, select, textarea')) return;
                        e.currentTarget.querySelector<HTMLAnchorElement>('a.cell-link')?.click();
                      }
                }
              >
                {conSelezione && (
                  <td className="col-check">
                    <input
                      type="checkbox"
                      aria-label={t('Seleziona la riga')}
                      checked={selezione.has(chiave)}
                      onChange={() => cambiaUna(chiave)}
                    />
                  </td>
                )}
                {colonne.map((colonna, indice) => (
                  <td
                    key={colonna.chiave}
                    // il titolo della colonna viaggia con la cella: sul
                    // telefono, dove l'intestazione non c'è, è l'unica cosa
                    // che dice cosa si sta leggendo
                    data-etichetta={colonna.titolo}
                    className={[
                      CLASSE_ALLINEAMENTO[colonna.allineamento ?? 'sinistra'],
                      colonna.monospazio ? 'num' : '',
                      colonna.larghezzaMax ? 'trunc' : '',
                      (colonna.mobile ?? (indice === 0 ? 'titolo' : undefined)) === 'titolo'
                        ? 'cella-titolo'
                        : '',
                      colonna.mobile === 'fuori' ? 'cella-fuori' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    style={colonna.larghezzaMax ? { maxWidth: colonna.larghezzaMax } : undefined}
                    // tagliato coi puntini, il testo intero resta a portata di mouse
                    title={
                      colonna.larghezzaMax && !colonna.cella
                        ? testoIntero((riga as Record<string, unknown>)[colonna.chiave])
                        : undefined
                    }
                  >
                    {colonna.cella
                      ? colonna.cella(riga)
                      : (((riga as Record<string, unknown>)[colonna.chiave] as ReactNode) ?? (
                          <span className="dim">—</span>
                        ))}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
